import React from "react";
import {
  Plus,
  FileText,
  ExternalLink,
  Trash2,
  FileCheck2,
  Shield,
  CreditCard,
} from "lucide-react";
import { StaffDetails, StaffDocumentTypeLabels } from "../../../types/staff.types";

interface DocumentsTabProps {
  details: StaffDetails | null;
  isEditing: boolean;
  editFormData: any;
  readOnly: boolean;
  onOpenDocUpload: () => void;
  onDeleteDoc: (docId: string) => void;
  onChange: (field: string, value: any) => void;
}

export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  details,
  isEditing,
  editFormData,
  readOnly,
  onOpenDocUpload,
  onDeleteDoc,
  onChange,
}) => {
  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="bg-slate-50/70 border border-slate-200/80 rounded-xl p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 border-b border-gray-200/80 pb-2.5">
            <CreditCard className="w-4 h-4 text-emerald-600" />
            <span>Government Identification Numbers</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Aadhaar Number
              </label>
              <input
                type="text"
                value={editFormData.aadhaarNumber || ""}
                onChange={(e) => onChange("aadhaarNumber", e.target.value)}
                placeholder="12-digit Aadhaar Number"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                PAN Number
              </label>
              <input
                type="text"
                value={editFormData.panNumber || ""}
                onChange={(e) => onChange("panNumber", e.target.value)}
                placeholder="10-character PAN (e.g. ABCDE1234F)"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 uppercase font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Pharmacy Registration Number
              </label>
              <input
                type="text"
                value={editFormData.pharmacyRegistrationNumber || ""}
                onChange={(e) => onChange("pharmacyRegistrationNumber", e.target.value)}
                placeholder="e.g. KA-PHARM-2022-8877"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Driving License Number
              </label>
              <input
                type="text"
                value={editFormData.drivingLicence || ""}
                onChange={(e) => onChange("drivingLicence", e.target.value)}
                placeholder="DL number"
                className="w-full px-3.5 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 text-gray-800 font-mono"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statutory Numbers Grid */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-gray-100 text-sm font-bold text-gray-900">
          <CreditCard className="w-4 h-4 text-emerald-600" />
          <span>Government IDs & Statutory Registrations</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <div>
            <p className="text-gray-400 font-medium">Aadhaar Number</p>
            <p className="text-gray-900 font-mono font-semibold text-sm mt-0.5">
              {details?.aadhaarNumber || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">PAN Number</p>
            <p className="text-gray-900 font-mono font-semibold text-sm mt-0.5">
              {details?.panNumber || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Pharmacy Reg. No.</p>
            <p className="text-gray-900 font-mono font-semibold text-sm mt-0.5">
              {details?.pharmacyRegistrationNumber || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-gray-400 font-medium">Driving License</p>
            <p className="text-gray-900 font-mono font-semibold text-sm mt-0.5">
              {details?.drivingLicence || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Uploaded Verification Files */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-2xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
            <FileCheck2 className="w-4 h-4 text-emerald-600" />
            <span>Uploaded Verification Files ({details?.documents?.length || 0})</span>
          </div>
          {!readOnly && (
            <button
              type="button"
              onClick={onOpenDocUpload}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Upload Document</span>
            </button>
          )}
        </div>

        {!details?.documents || details.documents.length === 0 ? (
          <div className="py-12 text-center border-2 border-dashed border-gray-200 rounded-xl">
            <FileText className="w-9 h-9 text-gray-300 mx-auto mb-2" />
            <p className="text-xs font-semibold text-gray-600">No documents uploaded yet</p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Upload Aadhaar, PAN, Pharmacy certificate, or address proofs
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {details.documents.map((doc) => (
              <div
                key={doc.id}
                className="p-3.5 rounded-xl border border-gray-200 bg-white hover:border-emerald-300 transition-all flex items-center justify-between shadow-2xs"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="text-xs font-bold text-gray-900 truncate">
                      {doc.documentName || StaffDocumentTypeLabels[doc.documentType as any] || doc.documentType}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {StaffDocumentTypeLabels[doc.documentType as any] || doc.documentType}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {doc.documentUrl && (
                    <a
                      href={doc.documentUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="p-1.5 text-gray-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors"
                      title="View / Download"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onDeleteDoc(doc.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete Document"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
